import React, { useState, useEffect, useCallback } from "react";

import {
  listDatasources,
  createDatasource,
  updateDatasource,
  deleteDatasource,
  listSchemas,
  listTables,
  listColumns,
} from "../../../../api";
import { useNotify } from "../../../../ToastContext";
import { usePermission } from "../../../../UserContext";
import Select from "../../../../components/Select";
import PasswordInput from "../../../../components/PasswordInput";
import NumberInput from "../../../../components/NumberInput";
import plusIcon from "../../../../assets/plus.svg";
import xmarkIcon from "../../../../assets/xmark.svg";
import moreIcon from "../../../../assets/more-horiz.svg";
import chevronIcon from "../../../../assets/chevron-right.svg";

const POLL_INTERVAL = 10000;

const DB_TYPES = [
  { value: "postgresql", label: "PostgreSQL" },
  { value: "mysql", label: "MySQL" },
  { value: "influxdb", label: "InfluxDB" },
  { value: "prometheus", label: "Prometheus" },
];
const SSL_MODES = [
  { value: "disable", label: "Disable" },
  { value: "require", label: "Require" },
];
const DEFAULT_PORTS = { postgresql: 5432, mysql: 3306, influxdb: 8086, prometheus: 9090 };

const INITIAL_FORM = {
  name: "", type: "postgresql", host: "", port: "5432",
  database: "", username: "", password: "", ssl_mode: "disable",
  org: "",
};

const STATUS_COLORS = {
  ready: { bg: "rgba(76,175,80,0.12)", color: "rgba(76,175,80,0.95)", border: "rgba(76,175,80,0.25)", dot: "#4caf50" },
  connecting: { bg: "rgba(255,193,7,0.12)", color: "rgba(255,193,7,0.95)", border: "rgba(255,193,7,0.25)", dot: "#ffc107" },
  failed: { bg: "rgba(244,67,54,0.12)", color: "rgba(244,67,54,0.95)", border: "rgba(244,67,54,0.25)", dot: "#f44336" },
};

const StatusDot = ({ status }) => {
  const c = STATUS_COLORS[status] || STATUS_COLORS.connecting;
  return (
    <span title={status} style={{ width: 8, height: 8, borderRadius: "50%", background: c.dot, flexShrink: 0 }} />
  );
};

const TYPE_ICONS = {
  postgresql: "PG",
  mysql: "My",
  influxdb: "IX",
  prometheus: "PR",
};

// ── Schema browser ──────────────────────────────────────────────────

const TYPE_LABELS = { table: "T", view: "V" };
const TYPE_COLORS_SCHEMA = { table: "rgba(80,140,255,0.7)", view: "rgba(156,120,255,0.7)" };
const errorMessage = (err, fb) => { const m = String(err?.message || err).trim(); return m || fb; };

const ColumnNode = ({ col }) => (
  <div className="gwSchemaLeaf">
    <span className="gwSchemaColName">{col.name}</span>
    <span className="gwSchemaColType">{col.type}</span>
    {col.nullable && <span className="gwSchemaColNull">null</span>}
  </div>
);

const TableNode = ({ dsName, schema, table }) => {
  const [open, setOpen] = useState(false);
  const [columns, setColumns] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadColumns = useCallback(async () => {
    setLoading(true); setError(null);
    try { setColumns((await listColumns(dsName, schema, table.name)) || []); }
    catch (err) { setColumns(null); setError(errorMessage(err, "Failed to load columns")); }
    finally { setLoading(false); }
  }, [dsName, schema, table.name]);
  const toggle = async () => { if (open) { setOpen(false); return; } if (!columns || error) await loadColumns(); setOpen(true); };
  return (
    <div>
      <button className="gwSchemaRow" type="button" onClick={toggle}>
        <img src={chevronIcon} alt="" className="gwIcon" style={{ width: 10, height: 10, transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s ease" }} />
        <span className="gwSchemaIcon" style={{ color: TYPE_COLORS_SCHEMA[table.type] || TYPE_COLORS_SCHEMA.table }}>{TYPE_LABELS[table.type] || "T"}</span>
        <span className="gwSchemaName">{table.name}</span>
      </button>
      {open && <div className="gwSchemaChildren">
        {loading && <span className="gwSchemaHint">Loading columns...</span>}
        {error && <div className="gwSchemaRetryRow"><span className="gwSchemaError">{error}</span><button className="gwSchemaRetryBtn" type="button" onClick={loadColumns}>Retry</button></div>}
        {!loading && !error && columns?.length === 0 && <span className="gwSchemaHint">No columns</span>}
        {!error && columns?.map((col) => <ColumnNode key={col.name} col={col} />)}
      </div>}
    </div>
  );
};

const SchemaNode = ({ dsName, schema }) => {
  const [open, setOpen] = useState(false);
  const [tables, setTables] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadTables = useCallback(async () => {
    setLoading(true); setError(null);
    try { setTables((await listTables(dsName, schema.name)) || []); }
    catch (err) { setTables(null); setError(errorMessage(err, "Failed to load tables")); }
    finally { setLoading(false); }
  }, [dsName, schema.name]);
  const toggle = async () => { if (open) { setOpen(false); return; } if (!tables || error) await loadTables(); setOpen(true); };
  return (
    <div>
      <button className="gwSchemaRow" type="button" onClick={toggle}>
        <img src={chevronIcon} alt="" className="gwIcon" style={{ width: 10, height: 10, transform: open ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s ease" }} />
        <span className="gwSchemaIcon" style={{ color: "rgba(255,193,7,0.8)" }}>S</span>
        <span className="gwSchemaName">{schema.name}</span>
      </button>
      {open && <div className="gwSchemaChildren">
        {loading && <span className="gwSchemaHint">Loading tables...</span>}
        {error && <div className="gwSchemaRetryRow"><span className="gwSchemaError">{error}</span><button className="gwSchemaRetryBtn" type="button" onClick={loadTables}>Retry</button></div>}
        {!loading && !error && tables?.length === 0 && <span className="gwSchemaHint">No tables</span>}
        {!error && tables?.map((t) => <TableNode key={t.name} dsName={dsName} schema={schema.name} table={t} />)}
      </div>}
    </div>
  );
};

const SchemaTree = ({ dsName, dsType }) => {
  const [schemas, setSchemas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeSystem, setIncludeSystem] = useState(false);
  const loadSchemas = useCallback(async (cancelledRef) => {
    setLoading(true); setError(null);
    try { const data = await listSchemas(dsName, { includeSystem: dsType === "mysql" && includeSystem }); if (!cancelledRef?.current) setSchemas(data || []); }
    catch (err) { if (!cancelledRef?.current) { setSchemas(null); setError(errorMessage(err, "Failed to load schemas")); } }
    finally { if (!cancelledRef?.current) setLoading(false); }
  }, [dsName, dsType, includeSystem]);
  useEffect(() => { const c = { current: false }; loadSchemas(c); return () => { c.current = true; }; }, [loadSchemas]);
  if (loading) return <div className="gwSchemaHint">Loading schemas...</div>;
  if (error) return <div className="gwSchemaRetryRow"><span className="gwSchemaError">{error}</span><button className="gwSchemaRetryBtn" type="button" onClick={() => loadSchemas()}>Retry</button></div>;
  return (
    <div>
      {dsType === "mysql" && <label className="gwHint" style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 8 }}><input type="checkbox" checked={includeSystem} onChange={(e) => setIncludeSystem(e.target.checked)} />Show system schemas</label>}
      {schemas?.length === 0 ? <div className="gwSchemaHint">No schemas found</div> : <div className="gwSchemaTree">{(schemas || []).map((s) => <SchemaNode key={s.name} dsName={dsName} schema={s} />)}</div>}
    </div>
  );
};

// ── Connection Modal ────────────────────────────────────────────────

const ConnectionModal = ({ editing, prefilled, onClose, onSaved }) => {
  const [form, setForm] = useState(() => {
    if (editing) {
      return {
        name: editing.name,
        type: editing.type || "postgresql",
        host: editing.host || "",
        port: String(editing.port || "5432"),
        database: editing.database || "",
        username: editing.username || "",
        password: "",
        ssl_mode: editing.ssl_mode || "disable",
        org: editing.options?.org || "",
      };
    }
    if (prefilled) {
      const type = prefilled.type || INITIAL_FORM.type;
      const port = prefilled.port != null
        ? String(prefilled.port)
        : String(DEFAULT_PORTS[type] || INITIAL_FORM.port);
      return {
        ...INITIAL_FORM,
        type,
        port,
        name: prefilled.name || "",
        host: prefilled.host || "",
        database: prefilled.database || "",
        username: prefilled.username || "",
        ssl_mode: prefilled.ssl_mode || INITIAL_FORM.ssl_mode,
        org: prefilled.org || "",
      };
    }
    return INITIAL_FORM;
  });
  const [submitting, setSubmitting] = useState(false);
  const notify = useNotify();
  const isEdit = !!editing;

  const set = useCallback((field) => (e) => {
    const value = typeof e === "string" ? e : e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "type" && DEFAULT_PORTS[value]) next.port = String(DEFAULT_PORTS[value]);
      return next;
    });
  }, []);

  const isInflux = form.type === "influxdb";
  const isPrometheus = form.type === "prometheus";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = isPrometheus
      ? { type: form.type, host: form.host }
      : isInflux
        ? { type: form.type, host: form.host, options: { org: form.org } }
        : { type: form.type, host: form.host, port: parseInt(form.port, 10) || 0, database: form.database, username: form.username, ssl_mode: form.ssl_mode };
    if (form.password) payload.password = form.password;
    try {
      if (isEdit) { await updateDatasource(form.name, payload); }
      else { payload.name = form.name; await createDatasource(payload); }
      notify({ type: "success", title: isEdit ? "Updated" : "Connected", message: `"${form.name}" ${isEdit ? "updated" : "created"}.` });
      onSaved();
      onClose();
    } catch (err) {
      notify({ type: "error", title: isEdit ? "Update Failed" : "Connection Failed", message: String(err.message || err) });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="gwOverlay" onClick={onClose}>
      <form className="gwModal" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit} style={{ width: 500 }}>
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">{isEdit ? `Edit: ${editing.name}` : "New Connection"}</div>
            <div className="gwModalSubtitle">{isEdit ? "Update the connection settings." : "Configure a new database connection."}</div>
          </div>
          <button className="gwModalClose" type="button" onClick={onClose}>
            <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
          </button>
        </div>
        <div className="gwModalBody">
          <div className="gwFieldRow">
            <div className="gwField">
              <label className="gwLabel">Connection Name</label>
              <input className="gwInput" placeholder="e.g. production-db" value={form.name} onChange={set("name")} required disabled={isEdit} autoFocus={!isEdit} />
            </div>
            <div className="gwField">
              <label className="gwLabel">Type</label>
              <Select value={form.type} onChange={set("type")} options={DB_TYPES} />
            </div>
          </div>
          {isInflux && (
            <>
              <div className="gwField">
                <label className="gwLabel">URL</label>
                <input className="gwInput" placeholder="e.g. http://localhost:8086" value={form.host} onChange={set("host")} required autoFocus={isEdit} />
                <div className="gwHint">Full URL to the InfluxDB instance.</div>
              </div>
              <div className="gwFieldRow">
                <div className="gwField">
                  <label className="gwLabel">API Token</label>
                  <PasswordInput placeholder={isEdit ? "Leave blank to keep current" : "Enter API token"} value={form.password} onChange={set("password")} />
                </div>
                <div className="gwField">
                  <label className="gwLabel">Organization</label>
                  <input className="gwInput" placeholder="e.g. my-org" value={form.org} onChange={set("org")} />
                </div>
              </div>
            </>
          )}
          {isPrometheus && (
            <>
              <div className="gwField">
                <label className="gwLabel">URL</label>
                <input className="gwInput" placeholder="e.g. http://localhost:9090" value={form.host} onChange={set("host")} required autoFocus={isEdit} />
                <div className="gwHint">Full URL to the Prometheus instance.</div>
              </div>
              <div className="gwField">
                <label className="gwLabel">Bearer Token</label>
                <PasswordInput placeholder={isEdit ? "Leave blank to keep current" : "Enter bearer token (optional)"} value={form.password} onChange={set("password")} />
                <div className="gwHint">Optional. Required if Prometheus is behind an authenticating proxy.</div>
              </div>
            </>
          )}
          {!isInflux && !isPrometheus && (
            <>
              <div className="gwFieldRow">
                <div className="gwField">
                  <label className="gwLabel">Host</label>
                  <input className="gwInput" placeholder="e.g. localhost" value={form.host} onChange={set("host")} required autoFocus={isEdit} />
                </div>
                <div className="gwField">
                  <label className="gwLabel">Port</label>
                  <NumberInput placeholder="5432" value={form.port} onChange={set("port")} min={1} max={65535} />
                </div>
              </div>
              <div className="gwField">
                <label className="gwLabel">Database</label>
                <input className="gwInput" placeholder="e.g. cs_history" value={form.database} onChange={set("database")} required />
              </div>
              <div className="gwFieldRow">
                <div className="gwField">
                  <label className="gwLabel">Username</label>
                  <input className="gwInput" placeholder="e.g. postgres" value={form.username} onChange={set("username")} />
                </div>
                <div className="gwField">
                  <label className="gwLabel">Password</label>
                  <PasswordInput placeholder={isEdit ? "Leave blank to keep current" : "Enter password"} value={form.password} onChange={set("password")} />
                </div>
              </div>
              <div className="gwField">
                <label className="gwLabel">SSL Mode</label>
                <Select value={form.ssl_mode} onChange={set("ssl_mode")} options={SSL_MODES} />
                <div className="gwHint">Controls the TLS negotiation mode with the database server.</div>
              </div>
            </>
          )}
        </div>
        <div className="gwModalFooter">
          <button className="gwGhostBtn" type="button" onClick={onClose}>Cancel</button>
          <button className="gwPrimaryBtn" type="submit" disabled={submitting}>
            {submitting ? "Connecting..." : isEdit ? "Save" : "Create Connection"}
          </button>
        </div>
      </form>
    </div>
  );
};

// ── Main Page ───────────────────────────────────────────────────────

const DatabaseConnectionsPage = () => {
  const canConfigure = usePermission("configure");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [proposed, setProposed] = useState(null);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [browsing, setBrowsing] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [menuStyle, setMenuStyle] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const notify = useNotify();

  const fetchConnections = useCallback(async () => {
    try {
      setConnections((await listDatasources()) || []);
    } catch (err) {
      notify({ type: "error", title: "Error", message: String(err.message || err) });
    } finally { setLoading(false); }
  }, [notify]);

  useEffect(() => {
    fetchConnections();
    const id = setInterval(fetchConnections, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchConnections]);

  // Refresh when the AI mutates a datasource.
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.resource === "datasources") fetchConnections();
    };
    window.addEventListener("gw-ai-resource-changed", handler);
    return () => window.removeEventListener("gw-ai-resource-changed", handler);
  }, [fetchConnections]);

  // AI propose-datasource: open the New Connection modal pre-filled.
  useEffect(() => {
    const handler = (e) => {
      const payload = e.detail?.payload;
      if (!payload || typeof payload !== "object") return;
      setProposed(payload);
      setEditing(null);
      setShowCreate(true);
    };
    window.addEventListener("gw-ai-propose-datasource-page", handler);
    return () => window.removeEventListener("gw-ai-propose-datasource-page", handler);
  }, []);

  const openMenu = (e, name) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setMenuStyle({ position: "fixed", top: `${Math.round(r.bottom + 4)}px`, right: `${Math.max(12, Math.round(window.innerWidth - r.right))}px` });
    setMenuOpen(menuOpen === name ? null : name);
  };

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      const menu = document.querySelector(".gwProjectMenu");
      if (menu?.contains(e.target)) return;
      setMenuOpen(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const handleDelete = (conn) => {
    setMenuOpen(null);
    setDeleteTarget(conn);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDatasource(deleteTarget.name);
      notify({ type: "success", title: "Removed", message: `"${deleteTarget.name}" deleted.` });
      setDeleteTarget(null);
      fetchConnections();
    } catch (err) {
      notify({ type: "error", title: "Error", message: String(err.message || err) });
    } finally {
      setDeleting(false);
    }
  };

  const hasConnections = connections.length > 0;

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">Database</div>
            <div className="gwPageSubtitle">
              {hasConnections ? `${connections.length} connection${connections.length !== 1 ? "s" : ""} configured` : "Configure database connections for tag history and data storage."}
            </div>
          </div>
          <button className="gwPrimaryBtn" type="button" onClick={() => setShowCreate(true)}
            disabled={!canConfigure}
            title={canConfigure ? undefined : "Requires configure permission"}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
            <span>Add Connection</span>
          </button>
        </div>

        {loading && !hasConnections && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--gw-muted)", fontSize: 13 }}>Loading connections...</div>
        )}

        {!loading && !hasConnections && (
          <div className="gwEmptyState">
            <div className="gwEmptyTitle">No connections yet</div>
            <div className="gwEmptyBody">Add a database connection to start storing tag history and running queries.</div>
            <div className="gwEmptyActions">
              <button className="gwPrimaryBtn" type="button" onClick={() => setShowCreate(true)}
                disabled={!canConfigure}
                title={canConfigure ? undefined : "Requires configure permission"}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} />
                <span>Add Connection</span>
              </button>
            </div>
          </div>
        )}

        {hasConnections && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">Connections</div>
            <div className="gwCardSectionBody" style={{ padding: 0 }}>
              {connections.map((conn) => (
                <div key={conn.name} data-gw-resource="datasources" data-gw-name={conn.name}>
                  <div className="umUserRow" style={{ cursor: "pointer" }}
                    onClick={() => conn.type !== "influxdb" && conn.type !== "prometheus" && setBrowsing(browsing === conn.name ? null : (conn.status === "ready" ? conn.name : null))}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                      background: conn.status === "ready" ? "rgba(76,175,80,0.1)" : conn.status === "failed" ? "rgba(244,67,54,0.1)" : "var(--gw-surface-2)",
                      border: `1px solid ${conn.status === "ready" ? "rgba(76,175,80,0.2)" : conn.status === "failed" ? "rgba(244,67,54,0.2)" : "var(--gw-border)"}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 11, fontWeight: 700, color: "var(--gw-text-secondary)",
                    }}>
                      {TYPE_ICONS[conn.type] || "DB"}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span className="umUserName">{conn.name}</span>
                        <StatusDot status={conn.status} />
                      </div>
                      <div className="umUserMeta">
                        {conn.type} &middot; {conn.type === "influxdb" || conn.type === "prometheus" ? conn.host : `${conn.host}:${conn.port}/${conn.database}`}
                        {conn.error && <span style={{ color: "var(--gw-danger)" }} title={conn.error}> &middot; {conn.error}</span>}
                      </div>
                    </div>
                    <button type="button" className="gwIconButton" style={{ flexShrink: 0 }}
                      onClick={(e) => openMenu(e, conn.name)}>
                      <img src={moreIcon} alt="More" className="gwIcon" style={{ width: 16, height: 16 }} />
                    </button>
                    {menuOpen === conn.name && (
                      <div className="gwProjectMenu" style={menuStyle} onClick={(e) => e.stopPropagation()}>
                        {conn.status === "ready" && conn.type !== "influxdb" && conn.type !== "prometheus" && (
                          <button className="gwProjectMenuItem" type="button"
                            onClick={() => { setMenuOpen(null); setBrowsing(browsing === conn.name ? null : conn.name); }}>
                            {browsing === conn.name ? "Close Browser" : "Browse Schema"}
                          </button>
                        )}
                        <button className="gwProjectMenuItem" type="button"
                          disabled={!canConfigure}
                          title={canConfigure ? undefined : "Requires configure permission"}
                          onClick={() => { setMenuOpen(null); setEditing(conn); }}>
                          Edit
                        </button>
                        <div className="gwProfileDivider" />
                        <button className="gwProjectMenuItem gwProjectMenuDanger" type="button"
                          disabled={!canConfigure}
                          title={canConfigure ? undefined : "Requires configure permission"}
                          onClick={() => handleDelete(conn)}>
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                  {browsing === conn.name && conn.status === "ready" && (
                    <div className="gwSchemaPanel">
                      <SchemaTree dsName={conn.name} dsType={conn.type} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {showCreate && (
        <ConnectionModal
          prefilled={proposed}
          onClose={() => { setShowCreate(false); setProposed(null); }}
          onSaved={fetchConnections}
        />
      )}
      {editing && <ConnectionModal editing={editing} onClose={() => setEditing(null)} onSaved={fetchConnections} />}

      {deleteTarget && (
        <div className="gwOverlay" onClick={() => setDeleteTarget(null)}>
          <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 400 }}>
            <div className="gwModalHeader">
              <div>
                <div className="gwModalTitle">Remove Connection</div>
                <div className="gwModalSubtitle">This action cannot be undone.</div>
              </div>
              <button className="gwModalClose" type="button" onClick={() => setDeleteTarget(null)}>
                <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
              </button>
            </div>
            <div className="gwModalBody">
              <p style={{ fontSize: 13, color: "var(--gw-text-secondary)", margin: 0 }}>
                Are you sure you want to remove <strong>{deleteTarget.name}</strong>? Any services using this connection will lose access.
              </p>
            </div>
            <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
              <button className="gwGhostBtn" type="button" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="gwDangerBtn" type="button" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Removing..." : "Remove Connection"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { DatabaseConnectionsPage };
